import React, { useEffect, useState } from "react";
import { ClipboardList, Search, FileText } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Input from "../../../components/Input/Input";
import Pagination from "../../../components/Pagination/Pagination";
import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./AuditLog.module.scss";

const AuditLog = () => {
  /* =========================
     STATE
  ========================== */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [detailsModal, setDetailsModal] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Filters
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [moduleId, setModuleId] = useState("");

  /* =========================
     TABLE COLUMNS
  ========================== */
  const columns = [
    { label: "Company", key: "company" },
    { label: "User", key: "user" },
    { label: "User Activity", key: "activity" },
    { label: "Module", key: "module" },
    { label: "Details", key: "details" },
    { label: "Date & Time", key: "datetime" },
    { label: "IP", key: "ip" },
  ];

  /* =========================
     FETCH AUDIT LOGS
  ========================== */
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);

      const url = `/activity-logs?page=${currentPage}&limit=${rowsPerPage}&search=${searchText}${
        moduleId ? `&moduleId=${moduleId}` : ""
      }`;

      try {
        const response = await apiGet("logging", url);

        if (response?.status === "success") {
          const { data, pagination, modules } = response.data;

          setLogs(
            data.map((log) => ({
              id: log.id,
              company: log.companyName,
              user: log.userName,
              activity: log.activity,
              datetime: log.createdAt,
              module: log.module,
              details: log.details
                ? log.details
                    .replace(/\s*(<br\s*\/?>|\/n|\\n|br)\s*/gi, "\n")
                    .replace(/\n+/g, "\n")
                    .trim()
                : "",
              ip: log.ipAddress,
            })),
          );

          setPagination(pagination);

          if (Array.isArray(modules)) {
            setModules(modules);
          }
        }
      } catch (error) {
        console.error("Failed to fetch audit logs", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [currentPage, rowsPerPage, searchText, moduleId]);

  /* RESET PAGE WHEN FILTERS CHANGE */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, moduleId]);

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(1);
  };

  /* =========================
     JSX
  ========================== */
  return (
    <div className={styles["admin-audit-logs__page"]}>
      {/* PAGE TITLE */}
      <div className={styles["admin-audit-logs__header"]}>
        <div className={styles["admin-audit-logs__icon-wrapper"]}>
          <ClipboardList size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-audit-logs__title-group"]}>
          <h2 className={styles["admin-audit-logs__title"]}>Audit Logs</h2>
          <p className={styles["admin-audit-logs__subtitle"]}>
            A complete record of user actions, updates, and system events.
          </p>
        </div>
      </div>

      <div className={styles["admin-audit-logs__card"]}>
        {/* FILTER SECTION */}
        <div className={styles["admin-audit-logs__filter-bar"]}>
          <div className={styles["admin-audit-logs__search-group"]}>
            <Input
              type="search"
              placeholder="Search Company Name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>

          <div className={styles["admin-audit-logs__status-group"]}>
            <Input
              type="select"
              placeholder="All Modules"
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              options={[
                { label: "All Modules", value: "" },
                ...modules.map((m) => ({
                  label: m.name,
                  value: m.id,
                })),
              ]}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className={styles["admin-audit-logs__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={logs}
              renderCell={(row, key) => {
                /* DATE & TIME */
                if (key === "datetime") {
                  const datetime = row.datetime;
                  if (!datetime) return "--";
                  const [date, time, meridian] = datetime.split(" ");
                  return (
                    <span>
                      {date}{" "}
                      <span className={styles["admin-audit-logs__time"]}>
                        {time} {meridian}
                      </span>
                    </span>
                  );
                }

                /* DETAILS */
                if (key === "details") {
                  if (!row.details) {
                    return (
                      <span
                        className={styles["admin-audit-logs__details-empty"]}
                      >
                        No details
                      </span>
                    );
                  }

                  const shortText =
                    row.details.length > 25
                      ? row.details.slice(0, 25) + "..."
                      : row.details;

                  return (
                    <span
                      className={styles["admin-audit-logs__details-link"]}
                      onClick={() => setDetailsModal(row.details)}
                      title="View full details"
                    >
                      <FileText size={14} strokeWidth={2.5} />
                      {shortText}
                    </span>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>

        {/* PAGINATION */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />

        {/* DETAILS MODAL */}
        {detailsModal && (
          <Modal title="Audit Details" onClose={() => setDetailsModal(null)}>
            <div className={styles["admin-audit-logs__modal-content"]}>
              {detailsModal}
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
